import { CSSObject } from '@mui/material';

import { IConnector } from '~services/types';

import { NetworkIds } from './rpcConfig';

export enum EOtherAsset {
  USDC = 'USDC',
  FRAX = 'FRAX',
  FXS = 'FXS',
  USDT = 'USDT',
  DAI = 'DAI',
  BAL = 'BAL',
  STAKED_4_POOL = 'STAKED_4_POOL',
  vwGMX = 'vwGMX',
  vwDPX = 'vwDPX',
  WETH = 'WETH',
  fsGLP = 'fsGLP',
}

export enum EVestaAsset {
  ETH = 'ETH',
  wstETH = 'wstETH',
  ARB = 'ARB',
  GMX = 'GMX',
  DPX = 'DPX',
  sGLP = 'GLP',
  gOHM = 'gOHM',
  renBTC = 'renBTC',
}

export enum ENativeAsset {
  VSTA = 'VSTA',
  VST = 'VST',
  bVSTA = 'bVSTA',
}

export enum ELPType {
  'VSTA-ETH' = 'VSTA-ETH',
  'VST-FRAX' = 'VST-FRAX',
  'VST-USDC-DAI-USDT' = 'VST-USDC-DAI-USDT',
  'VST-USDC' = 'VST-USDC',
}

export enum BoostedFarm {
  'VST-USDC' = 'Boosted-VST-USDC',
}

export enum VestaFarm {
  'VSTA-ETH' = 'Vesta-VSTA-ETH',
  'VST-FRAX' = 'Vesta-VST-FRAX',
}

export enum FraxFarm {
  VST_FRAX_GAUGE = 'Frax-VST_FRAX_GAUGE',
}

export enum BalFarm {
  'VST-USDC-DAI-USDT' = 'bal-VST-USDC-DAI-USDT',
  'V2-VST-USDC-DAI-USDT' = 'bal-V2-VST-USDC-DAI-USDT',
}

export type FarmTypes = VestaFarm | FraxFarm | BalFarm | BoostedFarm;

export const RewardedVaultsValues = [EVestaAsset.sGLP, EVestaAsset.GMX] as const;
export type RewardedVaults = (typeof RewardedVaultsValues)[number];

interface VestaAssetQueryLookupTable<T> {
  [index: string]: T;
}

export type AllVestaAssets = EVestaAsset | ENativeAsset | ELPType | EOtherAsset;
export type AllVestaSingleAssets = EVestaAsset | ENativeAsset | EOtherAsset;

export type ChainlinkAssets =
  | EOtherAsset.BAL
  | EOtherAsset.DAI
  | EOtherAsset.FRAX
  | EOtherAsset.FXS
  | EOtherAsset.USDC
  | EOtherAsset.USDT;

export const CHAINLINK_VALUES: Record<string, ChainlinkAssets> = {
  BAL: EOtherAsset.BAL,
  DAI: EOtherAsset.DAI,
  FRAX: EOtherAsset.FRAX,
  FXS: EOtherAsset.FXS,
  USDC: EOtherAsset.USDC,
  USDT: EOtherAsset.USDT,
};

export const ALL_ASSET_VALUES: Record<string, AllVestaAssets> = {
  ...EVestaAsset,
  ...ENativeAsset,
  ...ELPType,
  ...EOtherAsset,
};

export const ALL_NON_VAULT_SINGLE_ASSET_VALUES: Record<string, ENativeAsset | EOtherAsset> = {
  ...ENativeAsset,
  ...EOtherAsset,
};

export const ALL_SINGLE_ASSET_VALUES: Record<string, EVestaAsset | ENativeAsset | EOtherAsset> = {
  ...EVestaAsset,
  ...ENativeAsset,
  ...EOtherAsset,
};

export const ALL_LP_VALUES: any = { ...ELPType };

export const ALL_FARM_TYPES: Record<string, FarmTypes> = { ...VestaFarm, ...FraxFarm, ...BoostedFarm };

export interface IUpdatePayload<T, Asset = EVestaAsset> {
  asset: Asset;
  data: Partial<T>;
}

export interface AssetConfig<MultiCall, Contract> {
  address: (network: NetworkIds) => string;
  allowances?: (network: NetworkIds) => string[];
  coingeckoId?: string;
}

export interface AssetFarm {
  singleAssets: AllVestaSingleAssets[];
  poolName?: string;
  poolId?: string;
  farmType?: FarmTypes;
  priceFetch: { fn: (...args: any) => Promise<any>; needsSingleAssetPrices?: boolean };
}

export interface AssetStabilityPool {
  rewardName: EVestaAsset;
}

export type AssetFarms = {
  [index in ELPType]: AssetFarm;
};

export type AssetConfigs = {
  [index in AllVestaAssets]: AssetConfig<any, any>;
};

export interface IConnectorInfos {
  [index: string]: IConnector;
}

export interface IContractInfos {
  [index: string]: {
    address: string;
    abi: any;
  };
}

export interface INativeCurrency {
  name: string;
  symbol: string;
  decimals: number;
}

export enum InfuraNetworks {
  homestead = 'homestead',
  ropsten = 'ropsten',
  rinkeby = 'rinkeby',
  arbitrum = 'arbitrum',
  'arbitrum-rinkeby' = 'arbitrum-rinkeby',
}
export interface IRPCInfos {
  [index: number]: {
    chainId: string;
    chainName: string;
    nativeCurrency: INativeCurrency;
    rpcUrls: string[];
    blockExplorerUrls: string[];
    iconUrls: string[];
    infuraNetwork?: InfuraNetworks;
  };
}

export interface IContractAddressBank {
  [index: string]: {
    [index in NetworkIds]?: string;
  };
}

interface ITokenInfo {
  address: string;
  symbol: string;
  decimals: string;
  image: string;
}

function buildLookupAssetTable(): VestaAssetQueryLookupTable<AllVestaAssets> {
  const obj = {};
  for (const asset of Object.values(ALL_ASSET_VALUES)) {
    Object.assign(obj, { [asset.toLowerCase()]: asset });
  }
  return obj;
}

function buildLookupFarmTable(): VestaAssetQueryLookupTable<FarmTypes> {
  const obj = {};
  for (const farm of Object.values(ALL_FARM_TYPES)) {
    Object.assign(obj, { [farm.toLowerCase()]: farm });
  }
  return obj;
}

export const AssetQueryLookup: VestaAssetQueryLookupTable<AllVestaAssets> = buildLookupAssetTable();

export const FarmQueryLookup: VestaAssetQueryLookupTable<FarmTypes> = buildLookupFarmTable();
